import { z } from "zod";

import { Unit } from "@/types/enums/unit";
import { ORAL_MEDICATION_NAMES } from "@/types/enums/weight-loss-medications";
import { DoseManagementStatus } from "@/types/medication";

export const lotSelectionSchema = z
  .object({ type: z.literal("lot"), value: z.string() })
  .nullable();

export const doseComponentSchema = z
  .object({
    id: z.string().optional(),
    medicationId: z.string().optional(),
    name: z.string().min(1, "Medication name is required"),
    amount: z.coerce
      .number()
      .finite()
      .min(0.01, "Amount must be greater than 0"),
    unit: z.enum(Unit),
    lotSelection: lotSelectionSchema,
    stockAdministrationQuotas: z
      .record(z.string(), z.number().finite().nonnegative())
      .optional(),
    lotAllocationBasis: z
      .object({
        medicationId: z.string().optional(),
        locationId: z.string().nullable().optional(),
        providerId: z.string().nullable().optional(),
        compoundMedicationId: z.string().nullable().optional(),
        originalQuantity: z.number().finite().positive(),
        currentPlanKey: z.string().optional(),
        currentQuantity: z.number().finite().positive().optional(),
        currentStockFactors: z
          .record(z.string(), z.number().finite().positive())
          .optional(),
        stocks: z.array(
          z.object({
            stockId: z.string(),
            unit: z.enum(Unit),
            perAdministration: z.number().finite().positive(),
            originalAdministrations: z.number().finite().positive().optional(),
            originalTarget: z.number().finite().positive(),
            allocations: z.array(
              z.object({
                shipmentId: z.string(),
                quantity: z.number().finite().positive(),
              }),
            ),
          }),
        ),
      })
      .optional(),
    clinicalShipmentIds: z.array(z.string().min(1)).optional(),
    checkoutReuse: z.boolean().optional(),
    // Client-only scope of an ordinary medication-level receipt selection.
    singleLotSelection: z.boolean().optional(),
    correctActualInventoryUse: z.boolean().optional(),
    lotAllocations: z
      .array(
        z.object({
          shipmentId: z.string().min(1),
          stockId: z.string().min(1),
          quantity: z.number().finite().positive(),
          allowExtraYield: z.boolean().optional(),
          decisionKey: z.string().optional(),
        }),
      )
      .optional(),
    // Visibility is deliberate and scoped to the medication that requested it.
    includeDepletedMedicationId: z.string().optional(),
    quantity: z.coerce
      .number()
      .finite()
      .min(0.1, "Quantity must be greater than 0"),
    // Original physical inputs are not inventory credit; ledger metadata supplies that.
    inventoryBaseline: z
      .object({
        medicationId: z.string().optional(),
        stockId: z.string().optional(),
        hasTrackedInventory: z.boolean().optional(),
        untrackedLots: z.array(z.string()).optional(),
        automaticShipmentIds: z.array(z.string()).optional(),
        recordedLots: z
          .array(
            z.object({
              shipmentId: z.string().nullable(),
              lotNumber: z.string().nullable(),
              selectionMethod: z.string(),
            }),
          )
          .optional(),
        quantity: z.number(),
        amount: z.number(),
        unit: z.enum(Unit),
        dilutionId: z.string().nullable().optional(),
      })
      .optional(),
    inventoryDecisionKey: z.string().optional(),
    inventoryPlanError: z.string().optional(),
    // Left undefined, the API charts the medication's default dilution.
    dilutionId: z.string().nullable().optional(),
    // Which numbers the user typed, so a medication pick keeps them instead of
    // refilling the defaults. Lives in form state so it survives the row remount
    // when the catalog finishes loading. Never sent to the API.
    typed: z
      .object({ amount: z.boolean(), quantity: z.boolean() })
      .partial()
      .optional(),
  })
  // A component must name a catalog row by id. Submitting a bare name lets the API
  // create a medication to match it, which is how near-duplicate catalog entries
  // ("Semaglutide" vs "Semaglutide ") accumulate. Reported on `name` because that is
  // the field the picker renders an error for.
  .superRefine((component, ctx) => {
    if (
      component.lotAllocations &&
      new Set(
        component.lotAllocations.map((allocation) => allocation.shipmentId),
      ).size !== component.lotAllocations.length
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["lotSelection"],
        message: "Select each lot only once.",
      });
    }
    if (
      component.lotAllocations?.some(
        (allocation) =>
          allocation.allowExtraYield &&
          allocation.decisionKey !==
            JSON.stringify([
              component.inventoryDecisionKey,
              allocation.quantity,
            ]),
      )
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["lotSelection"],
        message: "Inventory changed. Choose lots again to review extra yield.",
      });
    }
    if (component.inventoryPlanError) {
      ctx.addIssue({
        code: "custom",
        path: ["lotSelection"],
        message: component.inventoryPlanError,
      });
    }
    if (!component.medicationId) {
      ctx.addIssue({
        code: "custom",
        path: ["name"],
        message: "Select a medication from the list.",
      });
    }
  });

// A location row in the form. The amount is text so it can be left blank.
export const doseLocationSchema = z.object({
  label: z.string(),
  amount: z
    .string()
    .refine(
      (value) => value.trim() === "" || Number(value) >= 0,
      "Enter a number",
    ),
});

export const doseFormSchema = z.object({
  components: z
    .array(doseComponentSchema)
    .min(1, "At least one component is required"),
  administrationLocation: z.string(),
  // Only used where the clinic records doses by location (see DoseEventFields).
  locations: z.array(doseLocationSchema).optional(),
  sideEffects: z.string(),
  doseDate: z.date().optional(),
  comment: z.string(),
  frequency: z.string(),
  providerId: z.string().optional(),
  managementStatus: z.nativeEnum(DoseManagementStatus).nullable(),
  compoundMedicationId: z.string().nullable().optional(),
});

export type DoseComponentFormValues = z.infer<typeof doseComponentSchema>;
export type DoseFormValues = z.infer<typeof doseFormSchema>;

export const emptyDoseComponent = (name = ""): DoseComponentFormValues => ({
  name,
  amount: 1,
  unit: Unit.MG,
  lotSelection: null,
  quantity: 1,
});

export const componentsAreOral = (
  components: DoseComponentFormValues[],
): boolean =>
  components.length > 0 &&
  components.every((component) =>
    ORAL_MEDICATION_NAMES.includes(component.name),
  );
